<template>
    <div>
        <CreateTestimonialDialog v-model="showAddDialog" @created="handleTestimonialCreated" />
        <SnackbarComponent v-model="snackbar.show" :message="snackbar.message" :color="snackbar.color" />

        <EditTestimonialDialog v-model="showEditDialog" :testimonial="selectedTestimonial"
            @updated="handleTestimonialUpdated" />

        <DeleteTestimonialDialog v-model="showDeleteDialog" :testimonial="selectedTestimonialToDelete"
            @deleted="handleTestimonialDeleted" />

        <UnpublishTestimonialDialog v-model="showUnpublishDialog" :testimonial="selectedTestimonialToUnpublish"
            @unpublished="handleTestimonialUnpublished" />
        <div class="table-container">
            <v-card flat>
                <v-data-table :headers="headers" :items="Testimonials" :items-per-page="50"
                    class="elevation-0 custom-data-table" :no-data-text="noDataText" :height="tableHeight" fixed-header
                    :loading="tableLoading" density="compact" @click:row="handleRowClick">
                    <template v-slot:top>
                        <div class="d-flex justify-space-between align-center pa-4">
                            <span class="text-h5">Testimonials</span>
                            <v-btn color="primary" prepend-icon="mdi-plus" @click="openAddDialog">Add
                                Testimonials</v-btn>
                        </div>
                    </template>
                    <template v-slot:[`item.testimonial_description`]="{ item }">
                        <v-tooltip location="top">
                            <template v-slot:activator="{ props }">
                                <div v-bind="props" class="testimonial-message-preview"
                                    v-html="truncateMessage(item.testimonial_description)"></div>
                            </template>
                            <span style="
                  max-width: 400px;
                  display: inline-block;
                  word-wrap: break-word;
                ">{{ getFullMessageText(item.testimonial_description) }}</span>
                        </v-tooltip>
                    </template>
                    <template v-slot:[`item.created_on`]="{ item }">
                        {{ formatTimestamp(item.created_on) }}
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-menu location="bottom">
                            <template v-slot:activator="{ props }">
                                <v-btn icon size="small" variant="text" v-bind="props" @click.stop>
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item title="Edit" @click.stop="openEditDialog(item)">
                                    <template v-slot:prepend>
                                        <v-icon color="primary" size="small">mdi-pencil</v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item v-if="item.testimonial_status === 'PUBLISHED'" title="Unpublish"
                                    @click.stop="openUnpublishDialog(item)">
                                    <template v-slot:prepend>
                                        <v-icon color="warning" size="small">mdi-eye-off</v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item v-if="item.testimonial_status === 'UNPUBLISHED'" title="Publish"
                                    @click.stop="openPublishDialog(item)">
                                    <template v-slot:prepend>
                                        <v-icon color="success" size="small">mdi-eye</v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item v-if="item.testimonial_status === 'UNPUBLISHED'" title="Delete"
                                    @click.stop="openDeleteDialog(item)">
                                    <template v-slot:prepend>
                                        <v-icon color="error" size="small">mdi-delete</v-icon>
                                    </template>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>
            </v-card>
        </div>
    </div>
</template>

<script>
import { edit_testimonial } from "@/graphql/mutations";
import EditTestimonialDialog from '@/components/Testimonials/Dailogs/EditTestimonialDialog.vue';
import GetTestimonials from '@/mixins/Testimonials/GetTestimonials';
import CreateTestimonialDialog from '@/components/Testimonials/Dailogs/CreateTestimonialDialog.vue';
import DeleteTestimonialDialog from '@/components/Testimonials/Dailogs/DeleteTestimonialDialog.vue';
import UnpublishTestimonialDialog from '@/components/Testimonials/Dailogs/UnpublishTestimonialDialog.vue';
import SnackbarComponent from "@/components/Extras/Snackbar.vue";
import { generateClient } from "aws-amplify/api";

const client = generateClient();

export default {
    name: "TestimonialsPage",
    mixins: [GetTestimonials],
    components: {
        CreateTestimonialDialog,
        EditTestimonialDialog,
        DeleteTestimonialDialog,
        UnpublishTestimonialDialog,
        SnackbarComponent
    },
    data() {
        return {
            tableLoading: false,
            windowHeight: window.innerHeight,
            currentPage: 1,
            headers: [
                {
                    title: "User Designation", key: "testimonial_user_job_description", sortable: true
                },
                { title: "Message", key: "testimonial_description", sortable: true },
                { title: "Status", key: "testimonial_status", sortable: true },
                { title: "Created Date", key: "created_on", sortable: true },
                { title: "Actions", key: "actions", sortable: false },
            ],
            showAddDialog: false,
            showEditDialog: false,
            showDeleteDialog: false,
            showUnpublishDialog: false,
            showViewsDialog: false,
            snackbar: {
                show: false,
                message: "",
                color: "success",
            },
            selectedTestimonial: null,
            selectedTestimonialToDelete: null,
            selectedTestimonialToUnpublish: null,
        };
    },
    computed: {
        Testimonials() {
            return this.testimonialsList || [];
        },
        noDataText() {
            return "No Testimonials found. Click 'Add Testimonials' to add one.";
        },
        tableHeight() {
            return this.windowHeight - 170;
        },
    },
    async mounted() {
        this.updateWindowHeight();
        window.addEventListener("resize", this.updateWindowHeight);
        this.tableLoading = true;
        await this.fetchTestimonials();
        this.tableLoading = false;
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.updateWindowHeight);
    },
    methods: {
        async openPublishDialog(item) {
            try {
                const input = {
                    testimonial_id: item.testimonial_id,
                    testimonial_status: "PUBLISHED",
                };

                const response = await client.graphql({
                    query: edit_testimonial,
                    variables: { input },
                });

                const responseData = JSON.parse(
                    response.data.edit_testimonial
                );

                if (responseData.status === "SUCCESS") {
                    this.snackbar = {
                        show: true,
                        message:
                            responseData.status_message ||
                            "Testimonial published successfully!",
                        color: "success",
                    };

                    await this.fetchTestimonials();
                } else {
                    this.snackbar = {
                        show: true,
                        message:
                            responseData.status_message ||
                            "Failed to publish testimonial",
                        color: "error",
                    };
                }
            } catch (error) {
                console.error(error);

                this.snackbar = {
                    show: true,
                    message:
                        error?.errors?.[0]?.message ||
                        error?.message ||
                        "An error occurred while publishing testimonial",
                    color: "error",
                };
            }
        },
        openDeleteDialog(item) {
            this.selectedTestimonialToDelete = item;
            this.showDeleteDialog = true;
        },
        handleRowClick(event, { item }) {
            this.openEditDialog(item);
        },
        openEditDialog(item) {
            this.selectedTestimonial = item;
            this.showEditDialog = true;
        },
        openUnpublishDialog(item) {
            this.selectedTestimonialToUnpublish = item;
            this.showUnpublishDialog = true;
        },
        async handleTestimonialCreated() {
            this.tableLoading = true;
            await this.fetchTestimonials();
            this.tableLoading = false;
        },

        async handleTestimonialUpdated() {
            this.tableLoading = true;
            await this.fetchTestimonials();
            this.tableLoading = false;
        },

        async handleTestimonialDeleted() {
            this.tableLoading = true;
            await this.fetchTestimonials();
            this.tableLoading = false;
        },

        async handleTestimonialUnpublished() {
            this.tableLoading = true;
            await this.fetchTestimonials();
            this.tableLoading = false;
        },
        formatTimestamp(timestamp) {
            const numeric = Number(timestamp);
            if (isNaN(numeric)) return "";
            const ms = numeric < 1e12 ? numeric * 1000 : numeric;
            const date = new Date(ms);
            if (isNaN(date.getTime())) return "";
            return date.toLocaleString("en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                // hour: "2-digit",
                // minute: "2-digit",
                // hour12: true,
            });
        },
        truncateMessage(message) {
            if (!message) return "";
            // Strip HTML tags for preview
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = message;
            const textContent = tempDiv.textContent || tempDiv.innerText || "";
            // Return first line (approximately 80 characters) with ellipsis
            if (textContent.length > 50) {
                return textContent.substring(0, 50) + "...";
            }
            return textContent;
        },
        getFullMessageText(message) {
            if (!message) return "";
            // Strip HTML tags to get plain text for tooltip
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = message;
            return tempDiv.textContent || tempDiv.innerText || "";
        },
        updateWindowHeight() {
            this.windowHeight = window.innerHeight;
        },
        openAddDialog() {
            this.showAddDialog = true;
        },
    },
};
</script>

<style scoped></style>